import { AmountText } from '@/components/AmountText';
import type { AskDraft, AskReviewRow } from '@/api/hooks/askSchemas';
import { decimalSum } from '@/features/transactions/decimal';
import { AskMatchRow } from './AskMatchRow';
import { LikelyRelatedTray } from './LikelyRelatedTray';

export function uniqueAskRows(draft: AskDraft): AskReviewRow[] {
  const seen = new Set<string>();
  return draft.review_rows.filter((row) => {
    if (seen.has(row.line_item_id)) return false;
    seen.add(row.line_item_id);
    return true;
  });
}

export function selectedAskRows(draft: AskDraft, acceptedIds: Set<string>): AskReviewRow[] {
  return uniqueAskRows(draft).filter((row) => acceptedIds.has(row.line_item_id));
}

function totalsByCurrency(rows: AskReviewRow[]) {
  const totals = new Map<string, string[]>();
  rows.forEach((row) => totals.set(row.currency, [...(totals.get(row.currency) ?? []), row.amount]));
  return [...totals.entries()].map(([currency, amounts]) => ({ currency, amount: decimalSum(amounts) }));
}

export function AskProposalBody({ draft, acceptedIds, onToggle }: { draft: AskDraft; acceptedIds: Set<string>; onToggle: (id: string) => void }) {
  const selected = selectedAskRows(draft, acceptedIds);
  const exact = draft.review_rows.filter((row) => row.review_bucket === 'exact');
  const likely = draft.review_rows.filter((row) => row.review_bucket === 'likely_related');
  const totals = totalsByCurrency(selected);
  const interpretation = draft.interpretation;
  return (
    <div className="flex flex-col gap-2.5" data-testid="ask-proposal-body">
      {draft.effective_scope.degraded_sources.map((source) => (
        <div key={`${source.label}-${source.status}`} className="rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm" role="note">
          {source.label}: {source.status}
        </div>
      ))}
      <section className="rounded-lg border border-border bg-card p-3" aria-labelledby="ask-review-heading">
        <h2 id="ask-review-heading" className="font-semibold">Review this request</h2>
        <p className="mt-1 text-sm text-muted-foreground">{draft.sentence}</p>
        {interpretation?.window && <p className="mt-1 text-sm text-muted-foreground">{interpretation.window.start} through {interpretation.window.end}</p>}
        {interpretation?.categories.length ? <p className="mt-1 text-sm text-muted-foreground">Categories: {interpretation.categories.join(', ')}</p> : null}
        {draft.effective_scope.accounts.length > 0 && <p className="mt-1 text-sm text-muted-foreground">Accounts: {draft.effective_scope.accounts.map((account) => account.last4 ? `${account.display_name} · ${account.last4}` : account.display_name).join(', ')}</p>}
      </section>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{selected.length} item{selected.length === 1 ? '' : 's'} selected</span>
        <span className="inline-flex flex-wrap items-center gap-2"><span>Selected total</span>{totals.length ? totals.map((total) => <AmountText key={total.currency} value={total.amount} currency={total.currency} variant="amount" />) : <span>$0.00</span>}</span>
      </div>
      {draft.message && <p role="status" className="text-sm text-muted-foreground">{draft.message}</p>}
      {exact.length > 0 && <div className="rounded-lg border border-border" data-testid="ask-exact-rows">{exact.map((row) => <AskMatchRow key={row.line_item_id} row={row} kept={acceptedIds.has(row.line_item_id)} onToggle={onToggle} />)}</div>}
      {exact.length === 0 && likely.length === 0 && <p className="rounded-lg border border-border p-3 text-sm text-muted-foreground">No writable items matched this request.</p>}
      <LikelyRelatedTray rows={likely} keptIds={acceptedIds} onToggle={onToggle} />
    </div>
  );
}

export default AskProposalBody;
